import React, { useEffect, useState } from "react"

import * as Sentry from "@sentry/react"

import { PermissionGuard } from "@/components/common/PermissionGuard"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { useDepots } from "@/hooks/useDepots"
import { useJobPostings } from "@/hooks/useJobPostings"
import {
  APPLICATION_FILTERS_STORAGE_KEY,
  readPersistedApplicationFilters,
} from "@/utils/applicationFilterState"

import ApplicationsContent from "../components/applications/ApplicationsContent"
import ApplicationsTabsList from "../components/applications/ApplicationsTabsList"
import ExternalContentFrame from "../components/applications/ExternalContentFrame"
import JobPostingsContent from "../components/job-postings/JobPostingsContent"
import MetricsContent from "../components/metrics/MetricsContent"
import DashboardLayout from "../layouts/DashboardLayout"
import { FilterState } from "../types/application"

type ApplicationTab = "applications" | "trial" | "orientation" | "vehicle" | "jobs" | "metrics"

const { logger } = Sentry
const sentryVerbose = import.meta.env.VITE_SENTRY_VERBOSE_UI === "true"

const Applications: React.FC = () => {
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban")
  const [activeTab, setActiveTab] = useState<ApplicationTab>("applications")
  const [filters, setFilters] = useState<FilterState>(readPersistedApplicationFilters)

  useEffect(() => {
    try {
      localStorage.setItem(APPLICATION_FILTERS_STORAGE_KEY, JSON.stringify(filters))
    } catch (_) {
      // ignore write errors
    }
  }, [filters])

  // Track page load for Applications dashboard
  useEffect(() => {
    Sentry.startSpan(
      {
        op: "page.load",
        name: "Applications Page Load",
      },
      (span) => {
        span.setAttribute("initial_tab", "applications")
        if (sentryVerbose) {
          logger.info("Applications page loaded")
        }
      },
    )
  }, [])

  // Fetch depots and job postings from Supabase
  const { data: depots, isLoading: loadingDepots } = useDepots()
  const { jobPostings: jobPostingsList, isLoadingJobPostings } = useJobPostings()

  return (
    <DashboardLayout>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-secondary">Driver Applications</h1>
      </div>

      <Tabs
        defaultValue="applications"
        className="mb-6"
        onValueChange={(value) => {
          const newTab = value as ApplicationTab

          if (sentryVerbose) {
            Sentry.startSpan(
              {
                op: "ui.click",
                name: "Applications Tab Change",
              },
              (span) => {
                span.setAttribute("from_tab", activeTab)
                span.setAttribute("to_tab", newTab)
                logger.debug(logger.fmt`Applications tab changed from ${activeTab} to ${newTab}`)
              },
            )
          } else {
            Sentry.addBreadcrumb({
              category: "ui",
              level: "info",
              message: "Applications tab change",
              data: { from: activeTab, to: newTab },
            })
          }

          setActiveTab(newTab)
        }}
      >
        <ApplicationsTabsList />

        <PermissionGuard resource="applications" scopes={["any", "own"]}>
          <TabsContent value="applications">
            <ApplicationsContent
              viewMode={viewMode}
              locations={depots}
              jobPostings={jobPostingsList || []}
              filters={filters}
              setFilters={setFilters}
              setViewMode={setViewMode}
              isLoading={loadingDepots || isLoadingJobPostings}
            />
          </TabsContent>
        </PermissionGuard>

        <PermissionGuard resource="jobs" scopes={["any"]}>
          <TabsContent value="jobs">
            <JobPostingsContent />
          </TabsContent>
        </PermissionGuard>

        <PermissionGuard resource="metrics">
          <TabsContent value="metrics">
            <MetricsContent />
          </TabsContent>
        </PermissionGuard>

        <PermissionGuard resource="trial_blocks">
          <TabsContent value="trial">
            <ExternalContentFrame contentType="trial" jobPostings={jobPostingsList || []} />
          </TabsContent>
        </PermissionGuard>

        <PermissionGuard resource="sessions">
          <TabsContent value="orientation">
            <ExternalContentFrame contentType="orientation" jobPostings={jobPostingsList || []} />
          </TabsContent>
        </PermissionGuard>

        <PermissionGuard resource="sessions">
          <TabsContent value="vehicle">
            <ExternalContentFrame contentType="vehicle" jobPostings={jobPostingsList || []} />
          </TabsContent>
        </PermissionGuard>
      </Tabs>
    </DashboardLayout>
  )
}

export default Applications
